import React, {useCallback, useMemo} from 'react';
import {useSearchParams} from 'react-router-dom';
import {getApprovalBreakdown} from '@/utils/approvalStatusUtils';
import {ContentSubmission} from '@/types/contentSubmission';
import ContentSubmissionCard, {
  type ContentSubmissionAction,
  useContentSubmissionActionMenuItems,
} from '@/components/content-submission-card';
import {CONTENT_SUBMISSION_ACTION} from '@/components/content-submission-card/contentSubmissionCardTypes';
import {PROFILE_SOURCE} from '@/components/creator-profile/config/profileSources';
import {PROFILE_SOURCE_QUERY_PARAM} from '@/components/creator-profile/hooks/use-profile-source';
import TransitionedOverlay from '@/components/transitioned-overlay';
import useDownload from '@/hooks/use-download/useDownload';
import {
  buildDownloadFileName,
  resolveDownloadUrl,
} from '@/hooks/use-download/utils';
import useContentSubmissionOffPlatformIdentityStore from '@/stores/contentSubmissionOffPlatformIdentityStore';

type ContentSubmissionCardItemProps = {
  contentSubmission: ContentSubmission;
  campaignId?: string;
  campaignNetwork?: string | null;
  campaignState?: string | null;
  onAction: (action: ContentSubmissionAction, id: string) => void;
  isAdmin?: boolean;
  isOffPlatform?: boolean;
  onMenuOpen?: (context: {creator_id?: string; flow_id: string}) => void;
  onActionSelected?: (
    action: ContentSubmissionAction,
    context: {creator_id?: string; flow_id: string}
  ) => void;
};

function ContentSubmissionCardItem({
  contentSubmission,
  campaignId,
  campaignNetwork,
  campaignState,
  onAction,
  isAdmin = false,
  isOffPlatform = false,
  onMenuOpen,
  onActionSelected,
}: ContentSubmissionCardItemProps) {
  const identity = useContentSubmissionOffPlatformIdentityStore((s) =>
    campaignId ? (s.identities[campaignId] ?? null) : null
  );

  const downloadUrl = resolveDownloadUrl(contentSubmission);
  const downloadFileName = downloadUrl
    ? buildDownloadFileName(contentSubmission, downloadUrl)
    : undefined;
  const {
    status: downloadStatus,
    progress: downloadProgress,
    download,
  } = useDownload({
    id: contentSubmission.id,
    url: downloadUrl,
    fileName: downloadFileName,
  });

  const roleAlreadyApproved = useMemo(() => {
    if (!isOffPlatform || !identity?.role) return false;
    const {approvedStepsNames} = getApprovalBreakdown(
      contentSubmission.approvalStatus
    );
    return approvedStepsNames.includes(identity.role);
  }, [isOffPlatform, identity, contentSubmission.approvalStatus]);

  const {items: actionMenuItems, onMenuClose} =
    useContentSubmissionActionMenuItems({
      isAdmin,
      analysisStatus: contentSubmission.latestVersionAnalysisStatus,
      isOffPlatform,
      kind: contentSubmission.kind,
      aasmState: contentSubmission.aasmState,
      archivedFromState: contentSubmission.archivedFromState,
      campaignState,
      roleAlreadyApproved,
      parentContentSubmissionId: contentSubmission.parentContentSubmissionId,
      downloadStatus: {status: downloadStatus, progress: downloadProgress},
      hasDownloadUrl: !!downloadUrl,
    });

  const handleActionsMenuClose = useCallback(() => {
    onMenuClose(contentSubmission);
  }, [onMenuClose, contentSubmission]);

  const handleActionSelected = useCallback(
    (
      action: ContentSubmissionAction,
      context: {creator_id?: string; flow_id: string}
    ) => {
      if (action === CONTENT_SUBMISSION_ACTION.DOWNLOAD) {
        download();
        return;
      }
      onActionSelected?.(action, context);
    },
    [onActionSelected, download]
  );

  const [searchParams] = useSearchParams();

  const profileLink =
    contentSubmission.profile?.id && !isOffPlatform
      ? `?${new URLSearchParams([
          ...searchParams.entries(),
          ['creatorProfileId', contentSubmission.profile.id],
          [PROFILE_SOURCE_QUERY_PARAM, PROFILE_SOURCE.ASSETS],
        ]).toString()}`
      : undefined;

  const isTransitioned = contentSubmission.previousAasmState != null;

  return (
    <TransitionedOverlay isTransitioned={isTransitioned}>
      <ContentSubmissionCard
        contentSubmission={contentSubmission}
        onClick={() => {}}
        onAction={onAction}
        campaignNetwork={campaignNetwork}
        actionMenuItems={isTransitioned ? [] : actionMenuItems}
        showAiScore={isAdmin}
        isOverdue={contentSubmission.dueSoon}
        onActionsMenuOpen={onMenuOpen}
        onActionsMenuClose={handleActionsMenuClose}
        profileLink={profileLink}
        onActionSelected={handleActionSelected}
      />
    </TransitionedOverlay>
  );
}

export default ContentSubmissionCardItem;
